import {
  collection,
  deleteDoc,
  doc,
  Firestore,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  type FirestoreDataConverter,
  type QuerySnapshot,
  type Unsubscribe,
} from 'firebase/firestore';
import {
  assertGroupCreationRequest,
  groupCreationRequestSchema,
  type GroupCreationRequest,
  type GroupCreationRequestWrite,
  nowIsoString,
  createSlug,
} from '@penticton-dev-events/shared';
import { getUserProfile } from '../../auth/api/userRepository';

const groupCreationRequestsCollection = (firestore: Firestore) =>
  collection(firestore, 'groupCreationRequests').withConverter(
    groupCreationRequestConverter,
  );

type GroupCreationRequestDocument = Omit<GroupCreationRequest, 'id'>;

const groupCreationRequestConverter: FirestoreDataConverter<
  GroupCreationRequest,
  GroupCreationRequestDocument
> = {
  toFirestore(request: GroupCreationRequest): GroupCreationRequestDocument {
    const { id: _ignoredId, summary, ...rest } = request;

    // Filter out undefined fields (Firestore doesn't allow undefined)
    const result: any = { ...rest };
    if (summary !== undefined) {
      result.summary = summary;
    }

    return result;
  },
  fromFirestore(snapshot): GroupCreationRequest {
    const payload = snapshot.data();
    return assertGroupCreationRequest({ id: snapshot.id, ...payload });
  },
};

/**
 * Subscribe to group creation requests (for admins)
 */
export const subscribeToGroupCreationRequests = (
  firestore: Firestore,
  handleSnapshot: (requests: GroupCreationRequest[]) => void,
  handleError: (error: Error) => void,
  statusFilter?: 'pending' | 'approved' | 'rejected',
): Unsubscribe => {
  let requestsQuery = query(
    groupCreationRequestsCollection(firestore),
    orderBy('requestedAt', 'desc'),
  );

  if (statusFilter) {
    requestsQuery = query(requestsQuery, where('status', '==', statusFilter));
  }

  return onSnapshot(
    requestsQuery,
    (snapshot: QuerySnapshot<GroupCreationRequest>) => {
      handleSnapshot(snapshot.docs.map(docSnapshot => docSnapshot.data()));
    },
    error => {
      handleError(error);
    },
  );
};

/**
 * Subscribe to user's own group creation requests
 */
export const subscribeToUserGroupCreationRequests = (
  firestore: Firestore,
  uid: string,
  handleSnapshot: (requests: GroupCreationRequest[]) => void,
  handleError: (error: Error) => void,
): Unsubscribe => {
  const requestsQuery = query(
    groupCreationRequestsCollection(firestore),
    where('requesterUid', '==', uid),
    orderBy('requestedAt', 'desc'),
  );

  return onSnapshot(
    requestsQuery,
    (snapshot: QuerySnapshot<GroupCreationRequest>) => {
      handleSnapshot(snapshot.docs.map(docSnapshot => docSnapshot.data()));
    },
    error => {
      handleError(error);
    },
  );
};

/**
 * Create a group creation request
 */
export const createGroupCreationRequest = async (
  firestore: Firestore,
  payload: Omit<GroupCreationRequestWrite, 'requestedAt' | 'status'>,
): Promise<GroupCreationRequest> => {
  const trimmedName = payload.name.trim();
  const providedSlug = payload.slug?.trim();
  const slugSeed = providedSlug && providedSlug.length > 0 ? providedSlug : trimmedName;
  const slug = createSlug(slugSeed);

  if (!slug) {
    throw new Error(
      'Unable to generate a slug for this group. Please adjust the name or slug.',
    );
  }

  const id = slug; // Use slug as document ID
  const docRef = doc(groupCreationRequestsCollection(firestore), id);

  // Check if a group with this slug already exists
  const existingGroup = await getDoc(doc(collection(firestore, 'groups'), id));
  if (existingGroup.exists()) {
    throw new Error(
      `A group with the name "${trimmedName}" already exists. Please choose a different name.`,
    );
  }

  // Check if a request with this slug already exists
  const existing = await getDoc(docRef);
  if (existing.exists()) {
    throw new Error(
      `A group creation request with this name/slug already exists. Please choose a different name.`,
    );
  }

  const request: GroupCreationRequest = assertGroupCreationRequest({
    id,
    name: trimmedName,
    slug,
    summary: payload.summary?.trim() || undefined,
    autoAcceptMembers: payload.autoAcceptMembers,
    requesterUid: payload.requesterUid,
    requesterName: payload.requesterName,
    requesterEmail: payload.requesterEmail,
    requestedAt: nowIsoString(),
    status: 'pending',
  });

  await setDoc(docRef, groupCreationRequestConverter.toFirestore(request));

  return request;
};

/**
 * Get a group creation request by ID
 */
export const getGroupCreationRequest = async (
  firestore: Firestore,
  requestId: string,
): Promise<GroupCreationRequest | null> => {
  const docRef = doc(groupCreationRequestsCollection(firestore), requestId);
  const snapshot = await getDoc(docRef);

  if (!snapshot.exists()) {
    return null;
  }

  return snapshot.data();
};

/**
 * Approve a group creation request (admin only)
 */
export const approveGroupCreationRequest = async (
  firestore: Firestore,
  requestId: string,
  adminUid: string,
  groupId: string,
): Promise<void> => {
  const docRef = doc(groupCreationRequestsCollection(firestore), requestId);

  // Fetch admin profile to get display name
  const adminProfile = await getUserProfile(firestore, adminUid);
  const reviewerName = adminProfile?.displayName || 'Admin';

  await updateDoc(docRef, {
    status: 'approved',
    reviewedBy: adminUid,
    reviewerName,
    reviewedAt: nowIsoString(),
    groupId: groupId,
  });
};

/**
 * Reject a group creation request (admin only)
 */
export const rejectGroupCreationRequest = async (
  firestore: Firestore,
  requestId: string,
  adminUid: string,
  reviewNote?: string,
): Promise<void> => {
  const docRef = doc(groupCreationRequestsCollection(firestore), requestId);

  // Fetch admin profile to get display name
  const adminProfile = await getUserProfile(firestore, adminUid);
  const reviewerName = adminProfile?.displayName || 'Admin';

  await updateDoc(docRef, {
    status: 'rejected',
    reviewedBy: adminUid,
    reviewerName,
    reviewedAt: nowIsoString(),
    reviewNote: reviewNote || undefined,
  });
};

/**
 * Delete a group creation request (admin only)
 */
export const deleteGroupCreationRequest = async (
  firestore: Firestore,
  requestId: string,
): Promise<void> => {
  const docRef = doc(groupCreationRequestsCollection(firestore), requestId);
  await deleteDoc(docRef);
};

/**
 * List all group creation requests (admin only)
 */
export const listGroupCreationRequests = async (
  firestore: Firestore,
  statusFilter?: 'pending' | 'approved' | 'rejected',
): Promise<GroupCreationRequest[]> => {
  let requestsQuery = query(
    groupCreationRequestsCollection(firestore),
    orderBy('requestedAt', 'desc'),
  );

  if (statusFilter) {
    requestsQuery = query(requestsQuery, where('status', '==', statusFilter));
  }

  const snapshot = await getDocs(requestsQuery);
  return snapshot.docs.map(docSnapshot => docSnapshot.data());
};
